// hooks/logic/generateWheelPayload.js
//
// ISP-9999: extracted verbatim from useSegmentSetting.js so the wheel SAVE
// payload builder (cell builder:14.11) can be unit-asserted in isolation. It is
// a PURE function of its three arguments plus module-level helpers — it reads no
// hook/component state — so the extraction is behaviour-preserving: the hook now
// delegates to this module and every existing caller keeps the same output.
import { convertKeysToSnakeCase } from "../../utils/helpers";
import { MINORS_POLICY_ENABLED } from "../../constants/featureFlags";
import { COMMENTS_ENABLED } from "../../utils/config";
import { isMatureRating } from "../../components/MatureContent/matureContent";
import { convertGlobalSettingsToApiFormat } from "../../utils/segmentBuilderUtils";
import {
  stripTransientSegmentFields,
  serializeSegmentNodes,
} from "../../utils/segmentPayloadHygiene";

export const generateWheelPayload = (
  wheel_information = {},
  wheelWallpaperSettings = {},
  // Wheel type is chosen once, at creation, and is permanent. Defaults to 2
  // (Standard) so every existing 2-arg caller — and the Standard/Timed flag
  // being OFF — persists exactly as before. The Create-Wheel wizard passes 1
  // (Timed) when the user picks Timed on the flag-gated first step.
  wheelType = 2
) => {
  let payload = {
    type: wheelType,
    take_screenshot: wheel_information.take_screenshot ?? false,
    wheel_preview: wheel_information.wheel_preview ?? "",
    version: wheel_information.version || 2,
    business_box: wheel_information.business_box?.business_title
      ? wheel_information.business_box
      : null,
    is_primary: wheel_information.is_primary ?? false,
    // Private (2), not Public (1), when the caller supplies no visibility —
    // see wheel-privacy-default-private.
    visibility: wheel_information.visibility ?? 2,
    palette_id: wheel_information.palette_id,

    is_locked: wheel_information.is_locked ?? false,
    show_business_box: wheel_information.show_business_box ?? true,

    wheel_name:
      wheel_information.wheel_name === "Wheel Name placeholder"
        ? ""
        : wheel_information.wheel_name,
    wallpaper_settings: {
      wallpaper_transparency:
        wheelWallpaperSettings.wallpaperTransparency ?? 50,
      wallpaper_resizing: wheelWallpaperSettings.wallpaperResizing ?? 20,
      wallpaper_overlay_color:
        wheelWallpaperSettings.wallpaperOverlayColor ?? "#ffffff",
      bg_wallpaper: wheelWallpaperSettings.bgWallpaper ?? "",
      is_pattern: wheelWallpaperSettings.isPattern ?? false,
      segment_bg_color: wheelWallpaperSettings.segmentBgColor ?? "#ffffff",
      current_bg: wheelWallpaperSettings.currentBg ?? 0,
      selected_color_type: wheelWallpaperSettings.selectedColorType ?? 1,
      pattern_color: wheelWallpaperSettings.patternColor ?? "#000000",
      wallpaper_hue_percentage: wheelWallpaperSettings.wallpaperHuePercentage ?? 0,
      wallpaper_hue_color: wheelWallpaperSettings.wallpaperHueColor ?? "#ffffff",
    },
    wheel_center_media: {
      type: wheel_information?.wheel_center_media?.type || "image",
      url: wheel_information?.wheel_center_media?.url || "",
      // ISP-9999: the poster still was silently DROPPED here, so a video
      // portrait saved with no poster and every fallback that depends on it
      // (reduced motion, offscreen pause, a video that fails to load) had
      // nothing to paint but white — the §12 "never a bare centre" violation
      // this unit fixes. PortraitTab already persists `poster` into redux
      // (setWheelPortrait) and the API shape already carries it; only this
      // payload builder forgot it. Additive: an image/GIF portrait has no
      // poster, so it serializes "" exactly as the server already stores.
      poster: wheel_information?.wheel_center_media?.poster || "",
    },
    show_segment_icon: wheel_information?.segment_icon ?? "blank", // Note the typo fix here from "segment_icon" to "show_segment_icon"
    description: wheel_information?.description ?? "",
    // ISP-9999 (gordon-0904-wheel-categories-multi5-plus-custom-tags):
    // `categories` (up to 5) is now the source of truth; `category` is
    // derived client-side too (belt-and-suspenders alongside the server-side
    // sync in WheelSerializer.validate) so any other BE read path that still
    // only looks at the legacy singular field sees the creator's first pick.
    category: wheel_information?.categories?.[0] ?? wheel_information?.category ?? null,
    categories: wheel_information?.categories ?? [],
    wheel_tags: (wheel_information?.wheel_tags ?? [])
      .map((tag) => (typeof tag === "string" ? tag : tag?.name))
      .filter(Boolean)
      .map((name) => ({ name })),
    sub_title:
      wheel_information?.sub_title === "Subtitle"
        ? ""
        : wheel_information?.sub_title,
    wheel_segments: (() => {
      if (!wheel_information || !wheel_information?.wheel_segments) return [];

      const segmentIds = new Set();
      return wheel_information?.wheel_segments.map((segment) => {
        let currentId = segment.id;
        // Ensure ID is a number for incrementing
        if (typeof currentId !== "number") {
          // If it's not a number, convert to a number or generate a new one if it's not convertible
          const parsedId = parseInt(currentId, 10);
          currentId = isNaN(parsedId) ? Date.now() : parsedId;
        }

        while (segmentIds.has(currentId)) {
          currentId += 1;
        }
        segmentIds.add(currentId);

        // EW-013: strip transient editor-only fields (e.g. `selected`) so a
        // segment click no longer registers as a persisted change and the
        // save payload stays free of view-state noise.
        const restSegment = stripTransientSegmentFields(segment);
        const segmentSettingsSnake = convertKeysToSnakeCase(segment.settings || {});
        return {
          ...restSegment,
          id: currentId,
          // EW-PERSIST: the backend's segment update path reads ONLY the
          // segment's TOP-LEVEL `title` (yuga_wheel/api/v2/serializers/
          // wheel.py: `activity.title = activity_data.get("title", ...)`) —
          // it never looks at `settings.title`. But every title edit
          // (useSegmentSetting.js "segmentTitle") writes ONLY
          // `segment.settings.title`; the top-level `title` on the redux
          // segment is a hydration echo (contentDirty.js
          // SEGMENT_HYDRATION_ECHO_KEYS) that is never resynced after that
          // edit. Left as `...restSegment`'s stale echo, a save would silently
          // discard the user's typed title. Re-derive it from `settings` here,
          // the same way the dirty-diff already does for its OWN comparison.
          title: segment?.settings?.title ?? restSegment.title,
          text_body: "",
          pattern_id: segment?.patternId ?? 0,
          resizeMode: segment?.resizeMode ?? "1",
          // QA-12: last-line guard — never persist a `blob:` media src. A blob
          // object-URL is tab-local and can't resolve for anyone else, so
          // saving it corrupts the media (an AI wheel once saved 8 blob video
          // srcs that played for no one). serializeSegmentNodes blanks any
          // blob src/thumbnail/poster AND (P5-R3) encodes the result EXACTLY
          // ONCE regardless of whether `segment.nodes` arrived as a plain
          // object (untouched-this-session segment) or already a JSON string
          // (the segment the user just edited on the canvas) — the old blind
          // `JSON.stringify(...)` double-encoded the latter, so a just-edited
          // block's content (e.g. a freshly-pasted video Embed URL) silently
          // vanished from the persisted JSON on save. See segmentPayloadHygiene.js.
          nodes: serializeSegmentNodes(segment.nodes),
          settings: {
            ...segmentSettingsSnake,
            wallpaper_hue_percentage: segment?.settings?.wallpaperHuePercentage ?? 0,
            wallpaper_hue_color: segment?.settings?.wallpaperHueColor ?? "#ffffff",
          },
        };
      });
    })(),
    background: wheel_information?.background || {
      type: "",
      value: "",
    },
    image_links: wheel_information?.image_links || [],
    video_links: wheel_information?.video_links || [],
    document_links: wheel_information?.document_links || [],
    audio_links: wheel_information?.audio_links || [],
    starred_links: wheel_information?.starred_links || [],
    lock_type: {
      description: "test lock type",
      access_code_lock: {
        is_active: wheel_information?.lock_type?.access_code_lock?.is_active,
        access_codes:
          wheel_information?.lock_type?.access_code_lock?.access_codes || [],
      },
      permission_lock: {
        is_active: wheel_information?.lock_type?.permission_lock?.is_active,
        // members: wheel_information?.lock_type?.permission_lock?.members || [],
        emails: wheel_information?.lock_type?.permission_lock?.emails || [],
      },
    },
    is_clonable: wheel_information?.is_clonable ?? true,
    // T22.153: the Access tab's "Allow comments" switch rides this PUT like
    // visibility does. Only an explicit false disables. Flag OFF omits the key,
    // so a flag-off save is byte-identical to before.
    ...(COMMENTS_ENABLED
      ? { comments_enabled: wheel_information?.comments_enabled !== false }
      : {}),
    // minors-13plus B.14 — the creator's maturity self-label rides the EXISTING
    // wheel PUT/PATCH rather than a second endpoint. Flag OFF (default) omits
    // the key entirely, so a flag-off save is byte-identical to today's body.
    // Flag ON always sends an explicit "general"/"mature" (never undefined), so
    // turning the toggle back OFF actually clears the label server-side.
    ...(MINORS_POLICY_ENABLED
      ? {
          content_rating: isMatureRating(wheel_information?.content_rating)
            ? "mature"
            : "general",
        }
      : {}),
    is_live: wheel_information?.is_live,
    is_draft: wheel_information?.is_draft,
    global_settings: convertGlobalSettingsToApiFormat(
      wheel_information?.globalSettings
    ),
    automatic_resizing: wheel_information?.automatic_resizing ?? false,
  };
  return payload;
};

export default generateWheelPayload;
